import { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import useQueryParams from "../../../../../../hooks/useQueryParams";
import { AdPlanContext } from "../../../../../../components/core/blocks/AdPlan/AdPlanContext";
import { useFetch } from "../../../../../../hooks/api";
import { Col, Container as Grid, Row, Nav } from "react-bootstrap";
import { Navigate, useNavigate, createFileRoute } from "@tanstack/react-router";
import moment from "moment";
import { DatesProvider } from "../../../../../../dates/DatesContext";
import { useDates } from "../../../../../../dates/DatesContext";
import { useDateFormatter } from "../../../../../../i18n/useDateFormatter";
import Loading from "../../../../../../components/core/blocks/Loading";
import getMetricSpec from "../../../../../../modules/merchandising/utils/metrics";
import { useModuleEnabled } from "../../../../../../redux/selectors/modules";
import { snakeCaseToSentenceCase } from "../../../../../../utils/formatText";
import MetricsCard from "../../../../../../components/custom/merchandise/MetricsCard";
import useMerchandisePreferences from "../../../../../../hooks/useMerchandisePreferences";
import { useTabsFromAdType } from "../../../../../../utils/getTabs";

import { formatPercent } from "../../../../../../utils/formatNumber";
import { useAcosOrRoas } from "../../../../../../utils/acosOrRoas";
import DetailedMetric from "../../../../../../components/core/blocks/DetailedMetric";
import LargeValue from "../../../../../../components/core/blocks/LargeValue";

import { AD_TYPES, AD_STATES, AD_STATUSES, GOALS } from "../../../../../../components/core/blocks/AdPlan/constants";
import useKeywordReport from "../../../../../../hooks/useKeywordReport";
import useCategories from "../../../../../../hooks/useCategories";
import useAdPlanState from "../../../../../../components/core/blocks/AdPlan/useAdPlanState";
import NTMStatusWidget from "../../../../../../components/core/blocks/AdPlan/NTMStatusWidget";
import { displayAdStatus } from "../../../../../../utils/displayAdStatus";
import SettingsSection from "../../../../../../modules/advertising/components/SettingsSection";
import MicroStores2 from "../../../../../../components/custom/category/advertisements/MicroStores2";
import { AdTargetTable } from "../../../../../../components/custom/category/advertisements/AdTargetTable";
import SearchTermReport from "../../../../../../components/core/blocks/AdPlan/SearchTermReport";
import AdTags from "../../../../../../modules/advertising/components/AdTags";
import { useAppSelector } from "../../../../../../redux/hooks";
import PlacementTable from "../../../../../../modules/advertising/components/PlacementTable";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import AudienceTable from "../../../../../../modules/advertising/components/AudienceTable";
import BudgetIncreaseModal from "../../../../../../modules/live/components/BudgetIncreaseModal";
import ProductAdsTable from "../../../../../../components/custom/category/advertisements/ProductAdsTable";
import { Flex } from "../../../../../../../styled-system/jsx";
import { css } from "../../../../../../../styled-system/css";
import { Button } from "../../../../../../components/core/basic/Button";
import ChatContextSetter from "../../../../../../components/chat/ChatContextSetter";
import { useSimpleCan } from "../../../../../../config/casl/casl";
import { useAdTotals } from "../../../../../../modules/perf_reports/api/ads/useAdTotals";
import { useAdPlanDetails } from "../../../../../../modules/perf_reports/api/ads/useAdPlanDetails";
import { useGetV4AdvReportByDate } from "../../../../../../modules/perf_reports/api/ads/v4AdvReportByDate";
import AdBreadcrumbs from "../../../../../../modules/advertising/components/AdBreadcrumbs";
import { AdvertisementHistory } from "../../../../../../modules/advertising/components/AdvertisementHistory";
import { MultiMarketplaceAdWarning } from "../../../../../../components/custom/category/advertisements/MultiMarketplaceAdWarning";

const Dashboard = () => {
  const { t } = useTranslation(["advertising"]);
  const { id: adId } = Route.useParams();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const hasBulkAddProductsEnabled = useModuleEnabled("BULK_ADD_PRODUCTS_TO_ADS");

  const navigate = useNavigate();
  const [tab, setTab] = useQueryParams("tab", "products");

  const { group, start, end, preStart, preEnd } = useDates();
  const longDate = useDateFormatter("long");
  const { showNewAdvertisingDashboard } = useMerchandisePreferences();
  const userCanUpdateAds = useSimpleCan("update", "Ad");
  const { activeAdCount } = useContext(AdPlanContext);
  const adIdNumber = Number(adId);

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    channel: channel,
    empty: true,
    ad: adId,
  };

  const adIdFilters = useMemo(
    () => [
      {
        id: "ad_id",
        value: { op: "=", value: adIdNumber },
      },
    ],
    [adIdNumber],
  );

  const adTotalsQuery = useQuery(
    useAdTotals({
      adId: adIdNumber,
    }),
  );

  const adByDateOptionsGetter = useGetV4AdvReportByDate();
  const adTimelineQuery = useQuery({
    ...adByDateOptionsGetter({
      filters: adIdFilters,
      search: "",
    }),
    placeholderData: keepPreviousData,
  });

  const adPlanQuery = useQuery(
    useAdPlanDetails({
      adId: adIdNumber,
    }),
  );

  const normalizedAdPlanData = useMemo(() => {
    const plan = adPlanQuery.data?.data?.[0];

    if (!plan) {
      return undefined;
    }

    return {
      ...plan,
      id: plan.id ?? plan.ad_id,
      ad_id: plan.ad_id ?? plan.id,
      category: plan.category ?? plan.category_id,
    };
  }, [adPlanQuery.data]);

  const isArchivedAd = useMemo(() => {
    if (!normalizedAdPlanData) {
      return false;
    }

    return normalizedAdPlanData.ad_status === AD_STATUSES.GV_ADV_ARCHIVED.value;
  }, [normalizedAdPlanData]);

  const renderArchivedAdDetails = useMemo(() => {
    return isArchivedAd && user.is_staff;
  }, [isArchivedAd, user.is_staff]);

  const data = useMemo(
    () => ({
      adplans: normalizedAdPlanData ? [normalizedAdPlanData] : [],
      total: adTotalsQuery.data?.data,
      date: adTimelineQuery.data?.data ?? [],
    }),
    [normalizedAdPlanData, adTotalsQuery.data, adTimelineQuery.data],
  );

  const adPlanData = data?.adplans?.[0];

  const isMultiMarketplaceAd = adPlanData?.multi_marketplace;

  const isLoading = adTotalsQuery.isLoading || adTimelineQuery.isLoading || adPlanQuery.isLoading;
  const isFetching = adTotalsQuery.isFetching || adTimelineQuery.isFetching || adPlanQuery.isFetching;
  const isError = adTotalsQuery.isError || adTimelineQuery.isError || adPlanQuery.isError;
  const isSuccess = adTotalsQuery.isSuccess && adTimelineQuery.isSuccess && adPlanQuery.isSuccess;

  const { ad_type, category: categoryId, is_composite, ad_goal, created_datetime, managed } = adPlanData ?? "";

  const { preferences } = useMerchandisePreferences();

  const { adPlan: adPlanParams, onChange } = useAdPlanState(
    {},
    marketPlace?.marketPlace,
    adPlanData?.category,
    adPlanData?.id,
    preferences.acos_or_roas === "roas",
    user.organization_type,
    isArchivedAd,
  );

  const { parent } = adPlanParams;

  const isNTMAd = adPlanData?.ad_goal === GOALS.IMPORTED_ADS.value;
  const NTMBidsAccepted = adPlanParams?.nonTrellisEnrolledAdAcceptBids;
  const NTMStartDate = adPlanParams?.nonTrellisEnrolledAdAcceptBidsStartDate;
  const NTMDaysActive = moment().diff(moment(NTMStartDate), "days");

  const hideDailyBudgetModule =
    (isNTMAd && !NTMBidsAccepted) || channel === "walmart" || adPlanData?.state !== AD_STATES.ACTIVE.value;

  const initialAdCreated = user.ad_count || activeAdCount;

  const { currentCategoryDetails: categoryDetails, categories: categoryData } = useCategories(
    categoryId,
    "advertising/dashboard",
  );
  const planDetailsTabs = useTabsFromAdType(
    is_composite ? AD_TYPES.CUSTOM.value : ad_type,
    ad_goal,
    is_composite || parent,
    categoryDetails?.leaf_category_name === "Unmanaged",
    managed,
    adPlanParams,
    isArchivedAd,
  );

  const groupedTabs = planDetailsTabs?.reduce((result, item) => {
    const group = item.groupKey;
    result[group] = result[group] || [];
    result[group].push(item);
    return result;
  }, {});

  const baseTargetKeys = [
    marketPlace.marketPlace,
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    preStart.format("YYYY-MM-DD"),
    preEnd.format("YYYY-MM-DD"),
    group,
    adId,
  ];

  const { keywordData, isKeywordDataLoading, isKeywordStatsLoading } = useKeywordReport(baseTargetKeys, adId, filters);

  // Target table data
  const { data: targetData, isLoading: isTargetDataLoading } = useFetch(
    [...baseTargetKeys, "keyword_report", "target"],
    `/api/data_report/keyword_report`,
    { ...filters, kw_report: false, target_type: "target" },
    {
      select: (d) => d.data,
      enabled: !!planDetailsTabs?.map((t) => t.key)?.includes("targets") && !isMultiMarketplaceAd,
      placeholderData: keepPreviousData,
      staleTime: 2 * 60 * 1000,
    },
  );

  // Placements table data
  const { data: adPlacementData, isLoading: isAdPlacementDataLoading } = useFetch(
    [...baseTargetKeys, "keyword_report", "placements"],
    `/api/data_report/keyword_report`,
    { ...filters, kw_report: false, target_type: "locations" },
    {
      select: (d) => d.data,
      enabled: !!planDetailsTabs?.map((t) => t.key)?.includes("placements") && !isMultiMarketplaceAd,
      placeholderData: keepPreviousData,
      staleTime: 2 * 60 * 1000,
    },
  );

  // Placements table data
  const { data: adItemData, isLoading: isAdItemDataLoading } = useFetch(
    [...baseTargetKeys, "keyword_report", "adItems"],
    `/api/data_report/keyword_report`,
    { ...filters, kw_report: false, target_type: "target" },
    {
      select: (d) => d.data,
      enabled: !!planDetailsTabs?.map((t) => t.key)?.includes("autoAds") && !isMultiMarketplaceAd,
      placeholderData: keepPreviousData,
      staleTime: 2 * 60 * 1000,
    },
  );

  if (
    [AD_TYPES.SPONSORED_BRAND.value, AD_TYPES.SPONSORED_BRAND_V2.value, AD_TYPES.SPONSORED_BRAND_VIDEO.value].includes(
      ad_type,
    ) &&
    tab === "products"
  ) {
    setTab("configuration");
  }

  // Archived ads only expose the Configuration and History groups, so the
  // default "products" tab is filtered out. Skip to Configuration (Media Plan).
  if (renderArchivedAdDetails && tab === "products") {
    setTab("configuration");
  }

  const acosOrRoas = useAcosOrRoas();

  const { overviewMetrics } = useMemo(() => {
    const roasMetrics = getMetricSpec("roas");
    const acosMetrics = getMetricSpec("acos");

    return {
      overviewMetrics: {
        columns: [],
        metrics: [
          getMetricSpec("ad_sales"),
          getMetricSpec("ad_spend"),
          acosOrRoas(acosMetrics, roasMetrics),
          getMetricSpec("clicks"),
          getMetricSpec("impressions"),
          getMetricSpec("ctr"),
          getMetricSpec("cpc"),
          getMetricSpec("conversion_rate"),
          getMetricSpec("serving_hours"),
        ],
      },
    };
  }, [acosOrRoas]);

  const currentAd = useMemo(() => {
    if (!adPlanData) {
      return undefined;
    }

    const id = adPlanData.ad_id ?? adPlanData.id;
    return {
      ...adPlanData,
      id,
      name: adPlanData.ad_name ?? adPlanData.name,
      href: `/user/advertising/dashboard/ads/${id}`,
    };
  }, [adPlanData]);

  if (!showNewAdvertisingDashboard) {
    return <Navigate to="/user/dashboard" replace />;
  }

  const displayAdTypeName = (value) => {
    let adTypeArray = [];

    for (const type in AD_TYPES) {
      adTypeArray.push(AD_TYPES[type]);
    }

    // Address conflict between SB v2 and old SP generic ad type values
    if (!adPlanData?.is_composite) {
      adTypeArray = adTypeArray.filter((entry) => entry !== AD_TYPES.SPONSORED_PRODUCT_GENERIC);
    }

    let adTypeLabel = adTypeArray.find((entry) => entry.value === value)?.label;

    // This avoids walmart SP being labelled as Custom
    if (value === AD_TYPES.CUSTOM.value && channel === "walmart") {
      adTypeLabel = AD_TYPES.SPONSORED_PRODUCT.label;
    }

    return adTypeLabel ? t(adTypeLabel) : "";
  };

  const showAddProductAdsModal =
    userCanUpdateAds &&
    hasBulkAddProductsEnabled &&
    [
      AD_TYPES.SPONSORED_PRODUCT.value,
      AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
      AD_TYPES.SPONSORED_DISPLAY.value,
      AD_TYPES.SPONSORED_DISPLAY_V2.value,
    ].includes(adPlanData?.ad_type) &&
    (!isNTMAd || (isNTMAd && NTMBidsAccepted));

  const renderTabContent = (tab) => {
    switch (tab) {
      case "products":
        return <ProductAdsTable adId={adPlanData?.id} showAddProductAdsModal={showAddProductAdsModal} />;
      case "keywords":
        return channel === "walmart" ?
            <MicroStores2
              tableId={"keyword"}
              targetData={keywordData}
              isLoading={isKeywordDataLoading}
              isStatsLoading={isKeywordStatsLoading}
              adId={adPlanData?.id}
              adPlanData={adPlanData}
              adPlanParams={adPlanParams}
              filters={filters}
              key={"keyword-targets-table"}
            />
          : <AdTargetTable
              adId={adPlanData?.id}
              adPlanData={adPlanData}
              adPlanParams={adPlanParams}
              targetType="keyword"
              key="keyword-targets-table"
            />;
      case "targets":
        return channel === "walmart" ?
            <MicroStores2
              tableId={"target"}
              targetData={targetData}
              isLoading={isTargetDataLoading}
              adId={adPlanData?.id}
              adPlanData={adPlanData}
              adPlanParams={adPlanParams}
              filters={filters}
              key={"product-targets-table"}
            />
          : <AdTargetTable
              adId={adPlanData?.id}
              adPlanData={adPlanData}
              adPlanParams={adPlanParams}
              targetType="target"
              key="product-targets-table"
            />;
      case "negative-keywords":
        return channel === "walmart" ?
            <MicroStores2
              tableId={"negative-keywords"}
              adId={adPlanData?.id}
              adPlanData={adPlanData}
              adPlanParams={adPlanParams}
              filters={filters}
              key={"negative-keywords-table"}
            />
          : <AdTargetTable
              adId={adPlanData?.id}
              adPlanData={adPlanData}
              adPlanParams={adPlanParams}
              targetType="negative-keyword"
              key="negative-keywords-table"
            />;
      case "placements":
        return (
          <MicroStores2
            tableId={"placements"}
            targetData={adPlacementData}
            isLoading={isAdPlacementDataLoading}
            adId={adPlanData?.id}
            adPlanData={adPlanData}
            adPlanParams={adPlanParams}
            filters={filters}
            key={"placements-targets-table"}
          />
        );
      case "autoAds":
        return (
          <MicroStores2
            tableId={"adItems"}
            targetData={adItemData}
            isLoading={isAdItemDataLoading}
            adId={adPlanData?.id}
            adPlanData={adPlanData}
            adPlanParams={adPlanParams}
            filters={filters}
            key={"adItems-targets-table"}
          />
        );
      case "searchTermReport":
        return <SearchTermReport adId={adPlanData?.id} adPlanParams={adPlanParams} />;
      case "campaignPlacements":
        return <PlacementTable advertisementId={adPlanData?.id} adType={adPlanData?.ad_type} />;
      case "audienceReports":
        return <AudienceTable advertisementId={adPlanData?.id} adType={adPlanData?.ad_type} />;
      case "configuration":
        return (
          <SettingsSection
            sections={groupedTabs["configuration"]}
            categoryId={categoryId}
            marketPlace={marketPlace}
            adPlanData={adPlanData}
            adPlanParams={adPlanParams}
            channel={channel}
            user={user}
            categoryDetails={categoryDetails}
            preferences={preferences}
            isAdArchived={isArchivedAd}
          />
        );
      case "history":
        return <AdvertisementHistory advertisementId={adPlanData?.id} />;
      default:
        return null;
    }
  };

  return (
    <Grid
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <ChatContextSetter
        context={`Advertisement Details Page: ${currentAd?.name} (${currentAd?.id}) type: ${displayAdTypeName(adPlanData?.ad_type)}, Category: ${categoryDetails?.leaf_category_name} (${categoryDetails?.id})`}
      />
      <Row>
        <Col xs={12} className="mb-4">
          <AdBreadcrumbs
            categoryData={categoryData}
            categoryDetails={categoryDetails}
            categoryId={categoryId}
            currentAd={currentAd}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {((!adPlanData && !isLoading) || (isArchivedAd && !renderArchivedAdDetails)) && (
            <Flex
              className={css({
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "250px",
                fontWeight: "normal",
                textAlign: "center",
              })}
            >
              <p>{t(($) => $.advertising["No media plan details could be found."])}</p>
              <p className={css({ maxWidth: "450px", marginBottom: "large" })}>
                {t(
                  ($) =>
                    $.advertising[
                      "It could have been created on a different profile or marketplace, or may have been archived."
                    ],
                )}
              </p>
              <Button
                onPress={() => {
                  navigate({ to: "/user/advertising/dashboard" });
                }}
              >
                {t(($) => $.advertising["Return to Dashboard"])}
              </Button>
            </Flex>
          )}
          {isLoading && <Loading />}
          {isError && <p>{t(($) => $.advertising["Error Loading Dashboard"])}</p>}
          {isSuccess && adPlanData && isArchivedAd && renderArchivedAdDetails && (
            <Flex
              className={css({
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "normal",
                textAlign: "center",
                padding: "large",
              })}
            >
              <strong className={css({ fontSize: "xlarge" })}>
                {t(($) => $.advertising["This media plan is archived."])}
              </strong>
              <p className={css({ marginBottom: "0" })}>
                {t(
                  ($) =>
                    $.advertising[
                      "Archived media plan details are only visible to staff and will not appear for other users."
                    ],
                )}
              </p>
            </Flex>
          )}
          {isSuccess && adPlanData && (!isArchivedAd || renderArchivedAdDetails) && (
            <Row style={{ marginBottom: "1rem" }}>
              <Col xs={12}>
                <MetricsCard
                  id={"advertisingDetails"}
                  mobileStyle
                  titleElement={
                    <div className="d-flex align-items-center">
                      <h3 className="me-5">
                        {adPlanData?.ad_name} ({adId})
                      </h3>
                      {userCanUpdateAds && !isMultiMarketplaceAd && <AdTags adPlanData={adPlanData} />}
                    </div>
                  }
                  totals={
                    initialAdCreated ?
                      {
                        data: data?.total,
                        loading: isFetching,
                      }
                    : undefined
                  }
                  timeline={{
                    data: data?.date || [],
                    loading: isFetching,
                  }}
                  initialSelectedMetric={["ad_sales", "ad_spend", "roas"]}
                  metrics={overviewMetrics.metrics}
                  group={group}
                  showMetricsSelector={true}
                  showAnnotations={true}
                  selectedAd={adId}
                  selectedCategory={adPlanData?.category}
                  byline={
                    initialAdCreated ?
                      <>
                        {longDate.format(start.toDate())}
                        {" - "}
                        {longDate.format(end.toDate())}
                        {isFetching && <i className="fa fa-spinner fa-spin"></i>}
                      </>
                    : null
                  }
                  context="mediaPlan"
                >
                  <Row className={is_composite ? "pt-5 d-flex justify-content-center" : "pt-5"}>
                    {isNTMAd && !NTMBidsAccepted && (
                      <Col xs={12} md={6} lg={{ span: 2, offset: 1 }} className={`mb-4 px-2`}>
                        <LargeValue
                          center
                          name={t(($) => $.advertising["Ad Status"])}
                          value={
                            <>
                              {(() => {
                                const sel = displayAdStatus(adPlanData?.state, adPlanData?.ad_status);
                                return sel ? t(sel) : "";
                              })()}
                              <p className="fs-4 pt-1">{t(($) => $.advertising["Ad not Managed by Trellis"])}</p>
                            </>
                          }
                        />
                      </Col>
                    )}
                    {isNTMAd && NTMBidsAccepted && (
                      <Col xs={12} md={6} lg={{ span: 2 }} className={`mb-4 px-2`}>
                        <LargeValue
                          center
                          name={t(($) => $.advertising["Ad Status"])}
                          value={
                            <>
                              {adPlanData?.state === AD_STATES.ACTIVE.value ?
                                t(($) => $.advertising["Optimized for {count, plural, one {# day} other {# days}}"], {
                                  count: NTMDaysActive,
                                })
                              : (() => {
                                  const sel = displayAdStatus(adPlanData?.state, adPlanData?.ad_status);
                                  return sel ? t(sel) : "";
                                })()
                              }
                            </>
                          }
                        />
                      </Col>
                    )}
                    {hideDailyBudgetModule ? null : (
                      <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                        <LargeValue
                          center
                          name={t(($) => $.advertising["Serving Status"])}
                          value={
                            adPlanData?.serving_status ?
                              snakeCaseToSentenceCase(adPlanData.serving_status)?.replace("Campaign", "")
                            : t(($) => $.advertising["Unknown"])
                          }
                        />
                        {userCanUpdateAds && !isMultiMarketplaceAd && (
                          <BudgetIncreaseModal
                            ad={adPlanData}
                            defaultSuggestedBudget={
                              (adPlanData?.daily_budget_overide ?? adPlanData?.daily_budget) ?
                                Math.ceil(adPlanData?.daily_budget * 1.2)
                              : 0
                            }
                            isUnmanagedAd={isNTMAd && !NTMBidsAccepted}
                          />
                        )}
                      </Col>
                    )}
                    {!isNTMAd && (
                      <Col
                        xs={12}
                        md={6}
                        lg={{ span: 2, offset: hideDailyBudgetModule ? 1 : 0 }}
                        className={`mb-4 px-2`}
                      >
                        <LargeValue
                          center
                          name={t(($) => $.advertising["Ad Status"])}
                          value={
                            <>
                              {(() => {
                                const sel = displayAdStatus(adPlanData?.state, adPlanData?.ad_status);
                                return sel ? t(sel) : "";
                              })()}
                              {created_datetime && !isNTMAd && adPlanData?.state === AD_STATES.ACTIVE.value ?
                                <p className="fs-4 pt-1">
                                  {t(($) => $.advertising["Created on {date}"], {
                                    date: longDate.format(moment(created_datetime).toDate()),
                                  })}
                                </p>
                              : null}
                            </>
                          }
                        />
                      </Col>
                    )}
                    <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                      <LargeValue
                        center
                        name={t(($) => $.advertising["Ad Type"])}
                        value={displayAdTypeName(adPlanData?.ad_type)}
                      />
                    </Col>
                    <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                      <DetailedMetric
                        center
                        metric={{
                          id: "budget",
                          name: t(($) => $.advertising["Monthly Budget"]),
                          format: "currency",
                          compact: false,
                          decimalPlaces: 0,
                          accessor: (data) => data.value,
                        }}
                        data={{ value: adPlanData?.monthly_budget }}
                        showTarget={false}
                      />
                    </Col>
                    {!is_composite && (
                      <>
                        {acosOrRoas(
                          <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                            <LargeValue
                              center
                              name={t(($) => $.advertising["Optimal ACOS Range"])}
                              value={`${formatPercent(adPlanData?.target_acos / 100, {
                                decimalPlaces: 0,
                              })} - ${formatPercent(adPlanData?.max_acos / 100, {
                                decimalPlaces: 0,
                              })}`}
                            />
                          </Col>,
                          <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                            <LargeValue
                              center
                              name={t(($) => $.advertising["Optimal ROAS Range"])}
                              value={`${+((1 / adPlanData?.max_acos) * 100).toFixed(2)} - ${+((1 / adPlanData?.target_acos) * 100).toFixed(2)}`}
                            />
                          </Col>,
                        )}
                      </>
                    )}
                    <Col xs={12} md={6} lg={2} className={`mb-4 px-2`}>
                      <DetailedMetric
                        center
                        metric={{
                          id: "products",
                          name: t(($) => $.advertising["Products"]),
                          format: "number",
                          compact: false,
                          decimalPlaces: 0,
                          accessor: (data) => data.value,
                        }}
                        data={{ value: adPlanData?.product_count }}
                        showTarget={false}
                      />
                    </Col>
                  </Row>
                  {isNTMAd && !isMultiMarketplaceAd && (
                    <Row>
                      <NTMStatusWidget
                        adPlan={adPlanParams}
                        onChange={onChange}
                        roas={preferences.acos_or_roas === "roas"}
                      />
                    </Row>
                  )}
                  {isMultiMarketplaceAd ?
                    <MultiMarketplaceAdWarning />
                  : <>
                      <Row>
                        <Col xs={12} className="mt-5 ph-5 mb-5 border-bottom fs-standard">
                          <Nav
                            variant="underline"
                            onSelect={(selectedTab) => {
                              return setTab(selectedTab);
                            }}
                            activeKey={tab}
                          >
                            {Object.entries(groupedTabs)
                              ?.map((entry) => ({
                                groupKey: entry[0],
                                groupLabel: entry[1][0].groupLabel,
                                keysToRender: entry[1],
                              }))
                              ?.sort((a, b) => {
                                const order = { configuration: 1, history: 2 };
                                return (order[a.groupKey] ?? 0) - (order[b.groupKey] ?? 0);
                              })
                              ?.map(({ keysToRender, groupKey, groupLabel }) => {
                                const key = keysToRender?.length === 1 ? keysToRender[0]?.key : groupKey;
                                return (
                                  <Nav.Item key={key}>
                                    <Nav.Link eventKey={key} style={{ whiteSpace: "nowrap" }}>
                                      {t(groupLabel)}
                                    </Nav.Link>
                                  </Nav.Item>
                                );
                              })}
                          </Nav>
                        </Col>
                      </Row>
                      <Row>
                        <Col xs={12}>{renderTabContent(tab)}</Col>
                      </Row>
                    </>
                  }
                </MetricsCard>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Grid>
  );
};

const AdvertisementDetails = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export const Route = createFileRoute("/_authenticated/user/advertising/dashboard/ads/$id")({
  component: AdvertisementDetails,
  staticData: { application: "Advertising" },
});
